// AdminQuestionOptions.js
import React, { useEffect, useState } from "react";
import { 
  Container, 
  Card, 
  CardBody,
  Input,
  Button,
  Row,
  Col,
  Spinner
} from "reactstrap";
import { withTranslation } from "react-i18next";
import { CheckCheck } from "lucide-react";

import { useParams } from "react-router-dom";

import axiosInstance from "utils/axiosInstance";


const AdminQuestionOptions = ({ t }) => {

  const { infoQuestionId } = useParams();

  const [question, setQuestion] = useState(null);
  const [savedOptions, setSavedOptions] = useState([]); // what's actually saved
  const [editingOptions, setEditingOptions] = useState([]); // local edits

  const [stats, setStats] = useState({});
  const [newOption, setNewOption] = useState("");

  const [answers, setAnswers] = useState([]);
  const [loadingAnswerId, setLoadingAnswerId] = useState(null);

  const [pendingChanges, setPendingChanges] = useState({});

  /*----------------------
  /* Fetch
  /*----------------------*/

  const fetchData = async () => {

    const qRes = await axiosInstance.get(
      `/knovia/infoQuestion/${infoQuestionId}`
    );
    //console.log('qRes:', qRes);
    setQuestion(qRes.data);
    setSavedOptions(qRes.data.statsOptions || []);
    setEditingOptions(qRes.data.statsOptions || []);
    

    const aRes = await axiosInstance.get(
      `/knovia/infoQuestion/${infoQuestionId}/answers`
    );
    setAnswers(aRes.data.sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt)));

    const sRes = await axiosInstance.get(
      `/knovia/infoQuestion/${infoQuestionId}/getStats`
    );
    console.log('sRes:', sRes);
    setStats(sRes.data || {});

  };

  useEffect(() => {
    fetchData();
  }, [infoQuestionId]);


  useEffect(() => {
    console.log('pendingChanges:', pendingChanges);
  }, [pendingChanges]);

  /*----------------------
  /* Edit Options List
  /*----------------------*/

  const addOption = () => {
    if (newOption && !editingOptions.includes(newOption)) {
      setEditingOptions([...editingOptions, newOption]);
      setNewOption("");
    }
  };

  const removeOption = (opt) => {
    setEditingOptions(editingOptions.filter(o => o !== opt));
  };

  const saveOptions = async () => {
    await axiosInstance.patch(
      `/knovia/infoQuestion/${infoQuestionId}/options`, 
      { options: editingOptions }
    );
    alert("Options updated.");
    fetchData(); 
  };


  /*----------------------
  /* Answers List
  /*----------------------*/

  const handleChangeResolvedOption = async (ans, newVal) => {
    setLoadingAnswerId(ans._id); // Start loading spinner
    try {
      await axiosInstance.patch(
        `/knovia/infoAnswer/${ans._id}/resolvedOption`,
        { resolvedStatsOption: newVal }
      );

      // Optimistically update UI
      setAnswers(prev =>
        prev.map(a =>
          a._id === ans._id ? { ...a, resolvedStatsOption: newVal } : a
        )
      );
    } catch (e) {
      console.error('error updating resolved option:', e);
    } finally {
      setLoadingAnswerId(null); // Stop spinner
      fetchData(); 
    }
    
  }

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Card>
            <CardBody>
              <h3>管理問題統計選項：{question?.label}</h3>

              <div className="mt-4 mb-5" 
                style={{ border: '1px solid grey', borderRadius: '10px', padding: '1rem 1.5rem' }}
              >
                <p>總回答數：{stats.totalResponses}</p>
                <ul>
                  {editingOptions.map((opt, i) => (
                    <li key={i} className="mb-1"> 
                      {opt} ({stats?.counts && stats.counts[opt] || 0} responses)
                      <Button color="outline-danger" className="ms-2"
                        onClick={()=>{removeOption(opt)}}>
                        -
                      </Button>
                    </li>
                  ))}
                  <li className="mb-1"> 
                    未分類 ({
                      stats.totalResponses -
                      editingOptions.reduce((sum, opt) => sum + (stats.counts?.[opt] || 0), 0)
                    } responses)
                    <Button color="outline-danger" className="ms-2" disabled={true}
                      onClick={()=>{removeOption(opt)}}>
                      -
                    </Button>
                  </li>
                </ul>

                <div className="d-flex" style={{ width:'30%' }}>
                  <Input
                    value={newOption}
                    onChange={(e) => setNewOption(e.target.value)}
                    placeholder="Add new option"
                  />
                  <Button color="outline-info" className="ms-2" onClick={addOption}>
                    Add
                  </Button>
                </div>

                <div className="d-flex justify-content-center">
                  <Button color="success" onClick={saveOptions}>Save All</Button>
                </div>
              </div>


              {/* Answers List */}
              <div className="mb-4">
                <h5 className="mt-4">使用者回覆列表（可手動修正統計分類）</h5>
              </div>
              <Row className="d-flex justify-content-center">
                <Col xs={8} md={6}>
                  {answers.map((ans, idx) => (
                    <Row key={ans._id} className="align-items-center mb-2">
                      <Col xs={4} md={6}>
                        <div style={{ wordBreak: 'break-all' }}>{ans.answer}</div>
                      </Col>
                      <Col xs={4} md={4}>
                        <Input
                          type="select"
                          value={pendingChanges[ans._id] ?? ans.resolvedStatsOption ?? ""}
                          onChange={(e)=>{
                            const newVal = e.target.value;
                            setPendingChanges(prev => ({ ...prev, [ans._id]: newVal }));
                          }}
                        >
                          {savedOptions.map((opt, i) => (
                            <option key={i} value={opt}>{opt}</option>
                          ))}
                          <option value="">未分類</option>
                        </Input>
                      </Col>

                      <Col xs={4} md={2}>
                        <Button
                          color="success"
                          className="d-flex justify-content-center align-items-center"
                          style={{ 
                            height: "33px",
                            width: "33px",
                            padding:"0px",
                          }}
                          disabled={
                            !((pendingChanges[ans._id] || pendingChanges[ans._id]==="")&& 
                              pendingChanges[ans._id] !== ans.resolvedStatsOption)
                          }
                          onClick={async () => {
                            await handleChangeResolvedOption(ans, pendingChanges[ans._id]);
                            setPendingChanges(prev => {
                              const copy = { ...prev };
                              delete copy[ans._id];
                              return copy;
                            });
                          }}
                        >
                          {loadingAnswerId === ans._id ? (
                            <Spinner style={{ color: "white", height: "20px", width: "20px", borderWidth: "1.5px" }} />
                          ) : (
                            <CheckCheck size={18} />
                          )}
                        </Button>
                      </Col>
                    </Row>
                  ))}
                </Col>
              </Row>


            </CardBody>
          </Card>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default withTranslation()(AdminQuestionOptions);



